import React, { useMemo, useState } from 'react';
import { Table, Form } from 'react-bootstrap';

function cellValue(column, row) {
  return column.value ? column.value(row) : row[column.key];
}

function compareValues(a, b) {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing || bMissing) {
    return aMissing === bMissing ? 0 : aMissing ? 1 : -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Table with client-side search and sorting, sized for a sale (hundreds of consigners, ~1,000 items).
 *
 * columns: [{ key, label, sortable?, value?(row), render?(row) }]
 *   value() supplies the text used for search and sort; render() controls what is displayed.
 */
export default function DataTable({
  columns,
  rows,
  getRowKey = row => row.id,
  searchable = false,
  searchPlaceholder = 'Search',
  onRowClick,
  emptyMessage = 'Nothing to show'
}) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: null, direction: 'asc' });

  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    let result = needle
      ? rows.filter(row => columns.some(column => {
        const value = cellValue(column, row);
        return value !== null && value !== undefined && String(value).toLowerCase().includes(needle);
      }))
      : rows;

    if (sort.key) {
      const column = columns.find(c => c.key === sort.key);
      const direction = sort.direction === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => compareValues(cellValue(column, a), cellValue(column, b)) * direction);
    }
    return result;
  }, [rows, columns, query, sort]);

  const toggleSort = key => {
    setSort(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <div>
      {searchable && (
        <Form.Control
          className="mb-2"
          type="search"
          placeholder={searchPlaceholder}
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
      )}
      <Table striped bordered hover size="sm" responsive>
        <thead>
          <tr>
            {columns.map(column => (
              <th
                key={column.key}
                onClick={column.sortable ? () => toggleSort(column.key) : undefined}
                style={column.sortable ? { cursor: 'pointer', whiteSpace: 'nowrap' } : undefined}
                aria-sort={sort.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                {column.label}
                {sort.key === column.key && (sort.direction === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center text-muted">{emptyMessage}</td>
            </tr>
          ) : (
            visibleRows.map(row => (
              <tr
                key={getRowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map(column => (
                  <td key={column.key}>{column.render ? column.render(row) : cellValue(column, row)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
}
